import { useRef, useState, useEffect, useCallback } from 'react'

interface UseCameraCaptureOptions {
  /** Called with an object-URL of the captured JPEG after the user takes a photo. */
  onCapture: (url: string) => void
}

interface UseCameraCaptureReturn {
  /** Whether the camera screen is currently visible. */
  isActive: boolean
  /** Ref to attach to the <video> element for the live stream. */
  videoRef: React.RefObject<HTMLVideoElement>
  /** Open the camera screen and start the stream. */
  open: () => void
  /** Close the camera screen and stop the stream without capturing. */
  close: () => void
  /** Capture the current frame, stop the stream, and call onCapture. */
  capture: () => void
}

export function useCameraCapture({ onCapture }: UseCameraCaptureOptions): UseCameraCaptureReturn {
  const [isActive, setIsActive] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
  }, [])

  // Start / stop the stream whenever isActive changes
  useEffect(() => {
    if (!isActive) return
    let cancelled = false

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.play()
        }
      })
      .catch(() => {
        if (!cancelled) setIsActive(false)
      })

    return () => {
      cancelled = true
      stopStream()
    }
  }, [isActive, stopStream])

  const open = useCallback(() => setIsActive(true), [])

  const close = useCallback(() => {
    stopStream()
    setIsActive(false)
  }, [stopStream])

  const capture = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth || 640
    canvas.height = video.videoHeight || 480
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        stopStream()
        setIsActive(false)
        onCapture(url)
      },
      'image/jpeg',
      0.92
    )
  }, [onCapture, stopStream])

  return { isActive, videoRef, open, close, capture }
}
